import clsx from "clsx";
import { omit } from "lodash";
import React, { memo, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ActionsModal,
  AllRowsSelector,
  Grid,
  GridRowExpandable,
  RenderWhen
} from "../../../../../components";
import { useClientSort, usePagination } from "../../../../../hooks";
import useExpandableRowsGrid from "../../../../../hooks/grid/useExpandableRowsGrid";
import { enums } from "../../../../../shim/jsbridge";
import { Sort } from "../../../../search/@types";
import { LOCATION } from "../../../../search/consts";
import { DroppedFilesGridData } from "../../../@types/rldynamicanalysis";
import {
  arrayToAnObject,
  createSelectedRowKey,
  isEmpty,
  mapClientSidePagedElements,
  objectContainsEvery,
  objectValues
} from "../../../util";
import ExpandedDetails from "../../expanded_details/containers/ExpandedDetails";
import { PAGE_SIZES } from "../consts";
import useActionsModal from "../../../hooks/useActionsModal";
import RlCloudSandboxDroppedFilesGridSchema from "./RlCloudSandboxDroppedFilesGridSchema";
import { pickLocalItems } from "./utils";
import { isMalwareConfigPath, malwareConfigStyles } from "../../malware_config";

const {
  threat_status: threatStatus,
  state: { ERROR }
} = enums;

type RlCloudSandboxDroppedFilesGridProps = {
  droppedFiles: Array<DroppedFilesGridData>;
};

const sortMapping = {
  _type: ["sample"],
  status: (file: DroppedFilesGridData) => [
    file.sample?.threat_status ??
      threatStatus[file.classification.toUpperCase()],
    Boolean(file.sample)
  ],
  threat_name: (file: DroppedFilesGridData) => [file.sample?.threat_name ?? ""],
  file_name: ["file_name", "sha1"],
  file_path: ["file_path"],
  sample_type: ["sample_type"],
  file_count: (file: DroppedFilesGridData) => file.sample?.files_count ?? "",
  sample_size: (file: DroppedFilesGridData) => file.sample_size ?? 0
};

const RlCloudSandboxDroppedFilesGrid: React.FC<
  RlCloudSandboxDroppedFilesGridProps
> = ({ droppedFiles }) => {
  // currently, we want to use hash only from local sample
  // that is why hash param is used here and not a hash as a prop
  const { hash } = useParams();
  const {
    sort,
    data: sortedData,
    onSortChange
  } = useClientSort<DroppedFilesGridData>(
    droppedFiles,
    { field: "status", direction: "desc" },
    sortMapping
  );
  const { data, updatePageData, currentPageData } = usePagination(
    sortedData || []
  );

  const [selectedRows, setSelectedRows] = useState<
    Record<string, DroppedFilesGridData>
  >({});
  const {
    expandedRows,
    handleCheckRowExpanded,
    handleRowExpandedChange,
    resetExpandedRows
  } = useExpandableRowsGrid(currentPageData.page || 0);

  const { modalState, onToggleModal } = useActionsModal();
  const isWholeTableSelected = useRef<boolean>(false);

  const locallyAvailableDroppedFiles = droppedFiles?.filter(
    file => file?.sample
  );

  useEffect(() => {
    updatePageData({
      newPage: 1
    });
  }, [sortedData, updatePageData]);

  const allRowsSelected = (
    currentPage: number,
    selectedRowItems: Record<string, DroppedFilesGridData>,
    tableData: Array<DroppedFilesGridData>
  ) => {
    if (!tableData?.length || isEmpty(selectedRows)) {
      return false;
    }

    const mappedElements = arrayToAnObject(tableData, currentPage);
    const localElements = pickLocalItems(mappedElements);
    return objectContainsEvery(localElements, selectedRowItems);
  };

  const addOrRemoveRowsFromSelected = (
    currentSelectedRows: Record<string, DroppedFilesGridData>
  ) => {
    if (objectContainsEvery(currentSelectedRows, selectedRows)) {
      setSelectedRows(prevState =>
        omit(prevState, Object.keys(currentSelectedRows))
      );
      isWholeTableSelected.current = false;
    } else {
      setSelectedRows(prevState => ({
        ...prevState,
        ...currentSelectedRows
      }));
    }
  };

  const handleAllRowsSelectedChange = (
    pageData: Array<DroppedFilesGridData>
  ) => {
    const mappedElements = arrayToAnObject(pageData, currentPageData.page);
    const localElement = pickLocalItems(mappedElements);
    addOrRemoveRowsFromSelected(localElement);
  };

  const checkRowSelected = (
    _itemRow: DroppedFilesGridData,
    rowIndex: number
  ) => {
    const rowKey = createSelectedRowKey(rowIndex, currentPageData.page);
    return rowKey in selectedRows;
  };

  const handleRowSelectChange = (
    rowItem: DroppedFilesGridData,
    rowIndex: number
  ) => {
    const rowKey = createSelectedRowKey(rowIndex, currentPageData.page);
    addOrRemoveRowsFromSelected({ [rowKey]: rowItem });
  };

  const isAllRowsOnPageSelected = allRowsSelected(
    currentPageData.page || 0,
    selectedRows,
    data
  );

  const renderExpandedRow = (rowItem: DroppedFilesGridData) => (
    <ExpandedDetails
      sha1={rowItem?.sample?.sha1 || ""}
      location={LOCATION.LOCAL}
    />
  );

  const setRowClassName = (rowItem: DroppedFilesGridData) =>
    clsx(
      rowItem?.sample && rowItem?.processing_status !== ERROR
        ? ""
        : "cursor-default",
      isMalwareConfigPath(rowItem) && malwareConfigStyles.annotated_row
    );

  const checkCheckboxVisible = (rowItem: DroppedFilesGridData) =>
    Boolean(rowItem.sample);

  const handlePageChange = (newPage: number, newPageSize: number) => {
    if (currentPageData.pageSize !== newPageSize) {
      setSelectedRows({});
      resetExpandedRows();
    }
    updatePageData({ newPage, newPageSize });
  };

  const handleSortChange = (_sort: Sort) => {
    setSelectedRows({});
    resetExpandedRows();
    onSortChange(_sort);
  };

  const handleRowExpandClick = (
    isExpanded: boolean,
    rowItem: DroppedFilesGridData,
    index: number
  ) => {
    if (!rowItem.sample || rowItem.processing_status === ERROR) return;
    handleRowExpandedChange(isExpanded, rowItem, index);
  };

  const handleSelectAllRowsChange = () => {
    isWholeTableSelected.current = !isWholeTableSelected.current;
    const newSelectedRows = isWholeTableSelected.current
      ? mapClientSidePagedElements(droppedFiles, currentPageData.pageSize)
      : {};
    const localElement = pickLocalItems(newSelectedRows);
    setSelectedRows(localElement);
  };

  return (
    <div className="samples-grid">
      <RenderWhen condition={!isEmpty(selectedRows)}>
        <AllRowsSelector
          allRowsSelected={isWholeTableSelected.current}
          totalResults={locallyAvailableDroppedFiles.length || 0}
          selected={objectValues(selectedRows)}
          onSelectAllRowsChange={handleSelectAllRowsChange}
        />
      </RenderWhen>
      <Grid
        allRowsSelected={isAllRowsOnPageSelected}
        checkCheckboxVisible={checkCheckboxVisible}
        checkRowExpanded={handleCheckRowExpanded}
        checkRowSelected={checkRowSelected}
        createSelectedRowKey={createSelectedRowKey}
        data={data}
        enablePageSizeSelector
        expandedRows={expandedRows}
        GridRowComponent={GridRowExpandable}
        isBulkCheckboxVisible={data?.some(item => item.sample)}
        numPages={currentPageData.numPages}
        page={currentPageData.page}
        pageSize={currentPageData.pageSize}
        pageSizeSelectorItems={PAGE_SIZES}
        renderExpandedRow={renderExpandedRow}
        rowClassName={setRowClassName}
        schema={RlCloudSandboxDroppedFilesGridSchema(
          isAllRowsOnPageSelected,
          selectedRows,
          hash,
          onToggleModal
        )}
        selectableRows
        showPageNumbers
        sort={sort}
        totalResults={currentPageData.totalResults}
        onPageChange={handlePageChange}
        onRowExpandedChange={handleRowExpandClick}
        onRowSelectChange={handleRowSelectChange}
        onSelectAllRowsChange={() => handleAllRowsSelectedChange(data)}
        onSortChange={handleSortChange}
      />
      <RenderWhen condition={Boolean(modalState?.name)}>
        <ActionsModal
          modalName={modalState.name}
          activeModalData={modalState.data}
          toggleModal={onToggleModal}
        />
      </RenderWhen>
    </div>
  );
};

export default memo(RlCloudSandboxDroppedFilesGrid);
